// PRD §3.2 — Item detail panel (the heaviest panel in the page). Slice 7
// covers the top portion:
//   - Header: numbered badge + serial chip + status badge + top-right Delete.
//   - Breadcrumb back to the parent Domain and SubArea.
//   - Item Name (max 200, required) + Description.
//   - Scope radios + info banner with a Disease Code Management link.
//   - Conditional Disease Group / Disease Code dropdowns.
//
// Measurement Schedule, Linked Questions, Grade Cutoff, and the
// [Save Draft] / [Save] action bar land in Slices 8–12. Local form state is
// already structured around a single `form` object so the next slices can
// add fields without rewiring the surface.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronRight, Folder, Info, PlusCircle, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { NumberInput } from '@/components/forms/NumberInput';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';
import type {
  DayOfWeek,
  FirstExecutionKind,
  GradeGuidelineMap,
  ProDomain,
  ProItem,
  ProItemDesignApiError,
  ProItemScope,
  ProMeasurementCycle,
  ProSchedule,
  ProSubArea,
} from '../types/pro-item-design.types';
import {
  DAYS_OF_WEEK,
  PRO_ITEM_SCOPES,
  PRO_MEASUREMENT_CYCLES,
} from '../types/pro-item-design.types';

import { useProItemDesignPageStore } from '../store/pro-item-design-page.store';
import {
  useCreateItem,
  useDeleteItem,
  usePublishItem,
  useSaveDraftItem,
} from '../hooks/use-pro-mutations';
import { isDraftId } from '../utils/draft-node';
import { buildInitialForm, type ItemFormState } from '../utils/item-form';
import { formToSavePayload } from '../utils/item-payload';
import {
  cycleExposesNDays,
  defaultNDaysForCycle,
  firstExecutionKindForCycle,
  requiresDaysOfWeek,
  validateItemForPublish,
} from '../utils/item-validation';
import {
  useDiseaseCodesAll,
  useDiseaseGroups,
} from '../hooks/use-disease-options';
import {
  domainNumber,
  itemNumber,
  subAreaNumber,
} from '../utils/numbering';
import { GradeCutoffSection } from './GradeCutoffSection';
import { LinkedQuestionsSection } from './LinkedQuestionsSection';
import { ProStatusBadge } from './ProStatusBadge';
import { NotFoundDetail } from './NotFoundDetail';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface ItemDetailProps {
  domain: ProDomain;
  domainIndex: number;
  subArea: ProSubArea;
  subAreaIndex: number;
  item: ProItem;
  itemIndex: number;
}

export function ItemDetail({
  domain,
  domainIndex,
  subArea,
  subAreaIndex,
  item,
  itemIndex,
}: ItemDetailProps) {
  const { t } = useTranslation('pro-item-design');
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const select = useProItemDesignPageStore((s) => s.select);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  const setDirty = useProItemDesignPageStore((s) => s.setDirty);
  const clearDraft = useProItemDesignPageStore((s) => s.clearDraft);
  const focusRequestId = useProItemDesignPageStore((s) => s.focusRequestId);
  const consumeFocus = useProItemDesignPageStore((s) => s.consumeFocus);
  // QC PID-018 — focus the name input when this item was just created.
  const nameRef = useRef<HTMLInputElement>(null);
  // SHCT-388 — a not-yet-persisted item (negative id) is created on the first
  // Save/Publish (two-step: createItem → saveDraft/publish).
  const isDraft = isDraftId(item.id);

  const createItem = useCreateItem();
  const deleteItem = useDeleteItem();
  const saveDraft = useSaveDraftItem();
  const publish = usePublishItem();
  const groups = useDiseaseGroups();
  const codes = useDiseaseCodesAll();
  const markItemDirty = useDeployDirtyStore((s) => s.addItem);

  // Defensive: a partially-hydrated item (e.g. a header node) may lack the
  // linked-questions list. Default to [] so the read-only table never crashes.
  const linkedQuestions = item.linkedQuestions ?? [];

  const [form, setForm] = useState<ItemFormState>(() => buildInitialForm(item));
  const [confirmOpen, setConfirmOpen] = useState(false);
  // PRD §8 — inline errors keyed by the BE/contract field path so the same
  // map can be hydrated from the client validator (`item-validation.ts`) or
  // from a server error response (`error.fields`). Values are ready-to-render
  // translated strings.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // PRD §3.4 — re-publish confirm shown before fired when the saved item is
  // already PUBLISHED. `afterPublishOpen` and `afterDeleteOpen` drive the
  // post-action InfoModals (PRO Deployment guidance).
  const [republishOpen, setRepublishOpen] = useState(false);
  const [afterPublishOpen, setAfterPublishOpen] = useState(false);
  const [afterDeleteOpen, setAfterDeleteOpen] = useState(false);
  // SHCT-388 — when a draft was just published, hold its real id so the
  // post-publish guidance modal can hand selection to the persisted item on
  // close (keeps this instance mounted until the mutation chain finishes).
  const [draftPublishedRealId, setDraftPublishedRealId] = useState<number | null>(null);

  // Reset when a new item id is selected, or when the cached item changes
  // (e.g. a save in another tab refreshes RQ). `item.updatedAt` covers
  // server-confirmed mutations without listing every individual field.
  useEffect(() => {
    setForm(buildInitialForm(item));
    setFieldErrors({});
    setDirty(false);
  }, [item.id, item.updatedAt, setDirty]);

  // QC PID-018 — a freshly-created item lands with the cursor in the name field.
  useEffect(() => {
    if (focusRequestId === item.id) {
      nameRef.current?.focus();
      nameRef.current?.select();
      consumeFocus(item.id);
    }
  }, [focusRequestId, item.id, consumeFocus]);

  // PRD §3.4 / §8 — required-field validation that gates PUBLISH. Recomputed
  // live so the Publish button disables the moment a required field is missing
  // (QC PID-053/055/057). Save Draft stays enabled (drafts allow incomplete).
  const publishErrorKeys = useMemo(() => validateItemForPublish(form), [form]);
  // SHCT-609 — drives both the required marker and the disabled state below.
  const daysRequired = requiresDaysOfWeek(form.cycle);
  const hasPublishErrors = Object.keys(publishErrorKeys).length > 0;
  // The distinct, human-readable reasons Publish is blocked — shown live in the
  // footer so the user sees *why* Save is disabled without having to click a
  // disabled button (e.g. an unconfigured Grade Cutoff). Deduped by message key.
  const publishBlockers = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const key of Object.values(publishErrorKeys)) {
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t(`item.validation.${key}`));
    }
    return out;
  }, [publishErrorKeys, t]);
  // QC PID-053 — the name is required; show the inline error live once it's
  // empty (a fresh item carries a default name, so this only fires on clear).
  const nameError =
    fieldErrors.name ??
    (!form.name.trim()
      ? t('item.validation.nameRequired')
      : form.name.length > 200
        ? t('item.validation.nameTooLong')
        : undefined);
  // QC PID-055 — N-day out of range shows an inline error live (mirrors nameError).
  const nDaysError =
    fieldErrors['schedule.firstExecution.nDays'] ??
    (form.firstExecutionKind === 'N_DAYS_AFTER' &&
    (form.firstExecutionNDays < 1 || form.firstExecutionNDays > 365)
      ? t('item.validation.nDaysRange')
      : undefined);

  const updateForm = (patch: Partial<ItemFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    // Clear stale errors on any of the patched paths so the user sees the
    // inline message disappear as they fix it.
    setFieldErrors((prev) => clearTouchedErrors(prev, patch));
    setDirty(true);
  };

  const onCycleChange = (cycle: ProMeasurementCycle) => {
    // Two cycles decide 요일 for the operator rather than accepting it, so the visible state
    // has to match what gets persisted:
    //
    //   DAILY     — the server fills all seven; mirror that (PRD 04 §3.2).
    //   QUARTERLY — the field is not used at all. PRD 04 **v1.24** and PRD 06 v1.17
    //               (회의 확정 2026-08-25) narrowed [ADM.056] from "선택(미지정 허용)" to
    //               removing 요일 from 분기별 on both the input and the display side: the
    //               schedule axis is `퇴원 후 N일` plus the fixed 90-day interval, and the
    //               calendar draws it at the default position (주 시작 = 월).
    //
    // Clearing matters because the picker only goes *disabled* for QUARTERLY. A 주 1회 item
    // with TUE selected, switched to 분기별, kept TUE in the payload — a value that plays no
    // part in scheduling, stored and later readable as though it did. That is the exact
    // misreading v1.24 gives as its reason for the change.
    //
    // SHCT-776 — 첫 시행 기준 is DERIVED from the cycle, never chosen, so it is set here on
    // the same edit that changes the cycle. The mapping lives in one exported helper that the
    // read-only display and the tests also call: a second copy is how the value shown on the
    // screen and the value saved to the item start disagreeing.
    //
    // N is re-seeded whenever the cycle actually changes to a different N-exposing cycle,
    // because 1 (매일) and 90 (분기별) are not interchangeable — carrying 1 over to 분기별
    // would silently schedule a quarterly item to start the day after discharge. Re-selecting
    // the SAME cycle leaves a hand-tuned N alone.
    const nextKind = firstExecutionKindForCycle(cycle);
    const seededNDays = defaultNDaysForCycle(cycle);
    updateForm({
      cycle,
      daysOfWeek:
        cycle === 'DAILY' ? [...DAYS_OF_WEEK] : cycle === 'QUARTERLY' ? [] : form.daysOfWeek,
      firstExecutionKind: nextKind,
      ...(seededNDays !== null && cycle !== form.cycle
        ? { firstExecutionNDays: seededNDays }
        : {}),
    });
  };

  const onToggleDay = (day: DayOfWeek) => {
    // Disabled whenever the weekday is not the operator's to choose — DAILY (BE
    // auto-fills all 7) or QUARTERLY (90-day arithmetic ignores 요일, SHCT-609).
    // Guard against direct calls.
    if (!requiresDaysOfWeek(form.cycle)) return;
    const has = form.daysOfWeek.includes(day);
    const next = has
      ? form.daysOfWeek.filter((d) => d !== day)
      : [...form.daysOfWeek, day];
    // Preserve canonical Mon→Sun order for display + diffing.
    next.sort(
      (a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b),
    );
    updateForm({ daysOfWeek: next });
  };

  const onNDaysChange = (n: number) => {
    if (!Number.isFinite(n)) return;
    // QC PID-055 — keep the raw value (no clamp) so an out-of-range entry (0/366)
    // surfaces the inline range error and blocks publish, instead of being
    // silently corrected to a passing value.
    updateForm({ firstExecutionNDays: Math.floor(n) });
  };

  const dn = domainNumber(domainIndex);
  const sn = subAreaNumber(domainIndex, subAreaIndex);
  const inum = itemNumber(domainIndex, subAreaIndex, itemIndex);

  // Breadcrumb crumbs are user-initiated → guarded (Slice 13).
  const onSelectDomainCrumb = () =>
    requestSelect({ type: 'domain', id: domain.id });
  const onSelectSubAreaCrumb = () =>
    requestSelect({ type: 'subArea', id: subArea.id, domainId: domain.id });

  const onConfirmDelete = () => {
    if (isDraft) {
      // SHCT-388 — a draft item was never persisted; delete just discards it.
      setConfirmOpen(false);
      clearDraft();
      select(null);
      return;
    }
    const wasPublished = item.status === 'PUBLISHED';
    const pid = item.pid;
    deleteItem.mutate(item.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(t('toasts.deleted'));
        // PRD §3.4 — deleting a PUBLISHED item marks it dirty and surfaces the
        // PRO Deployment guidance. Drafts are not visible in the Patient App
        // so no guidance is shown.
        if (wasPublished) {
          markItemDirty(pid);
          setAfterDeleteOpen(true);
        } else {
          select(null);
        }
      },
      onError: (e) => {
        setConfirmOpen(false);
        const err = e as ProItemDesignApiError;
        toast.error(err?.message || t('toasts.deleteItemFailed'));
      },
    });
  };

  // Maps a server `error.fields` map (paths → already-translated messages)
  // into the local fieldErrors state so the inline errors echo what BE saw.
  const hydrateServerFieldErrors = (
    fields: Record<string, string> | undefined,
  ) => {
    if (!fields) return;
    setFieldErrors((prev) => ({ ...prev, ...fields }));
  };

  // PRD §8 (line 260) — an item with no linked questions can still be saved,
  // but the user is warned (QC PID-059). Fires on both Save Draft and Publish.
  const warnIfNoLinkedQuestions = () => {
    if (linkedQuestions.length === 0) {
      toast.warning(t('item.validation.noLinkedQuestions'));
    }
  };

  // SHCT-388 — persist a draft item then run its detail save/publish. The BE
  // createItem only takes the name, so the full form is written in a second
  // call. Selection stays on the draft (this instance stays mounted) until the
  // chain finishes; only then do we hand off to the real id.
  const persistDraftThenSave = (mode: 'draft' | 'publish') => {
    warnIfNoLinkedQuestions();
    const payload = formToSavePayload(form);
    const onPersistError = (e: unknown) => {
      const err = e as ProItemDesignApiError;
      if (err?.fields && Object.keys(err.fields).length > 0) {
        hydrateServerFieldErrors(err.fields);
        return;
      }
      toast.error(
        err?.message ||
          (mode === 'publish' ? t('toasts.saveFailed') : t('toasts.draftSaveFailed')),
      );
    };
    createItem.mutate(
      { subAreaId: subArea.id, input: { name: form.name.trim() } },
      {
        onSuccess: (created) => {
          const realId = created.id;
          if (mode === 'draft') {
            saveDraft.mutate(
              { id: realId, input: payload },
              {
                onSuccess: () => {
                  setDirty(false);
                  clearDraft();
                  select({
                    type: 'item',
                    id: realId,
                    domainId: domain.id,
                    subAreaId: subArea.id,
                  });
                  toast.success(t('toasts.draftSaved'));
                },
                onError: onPersistError,
              },
            );
            return;
          }
          publish.mutate(
            { id: realId, input: payload },
            {
              onSuccess: (updated) => {
                setDirty(false);
                setFieldErrors({});
                markItemDirty(updated.pid);
                // Defer the hand-off to the real id until the guidance modal
                // closes (see onAfterPublishClose).
                setDraftPublishedRealId(realId);
                setAfterPublishOpen(true);
                toast.success(t('toasts.saved'));
              },
              onError: onPersistError,
            },
          );
        },
        onError: onPersistError,
      },
    );
  };

  // PRD §3.4 — Save Draft. BE skips required-field validation; field-level
  // errors (out-of-range weights, N-days, etc.) still come back as typed
  // errors from the API layer. Client-side validation does NOT run here.
  const onSaveDraft = () => {
    if (isDraft) {
      persistDraftThenSave('draft');
      return;
    }
    warnIfNoLinkedQuestions();
    saveDraft.mutate(
      { id: item.id, input: formToSavePayload(form) },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success(t('toasts.draftSaved'));
        },
        onError: (e) => {
          const err = e as ProItemDesignApiError;
          // §7.1 — field-scoped errors (e.g. duplicate name, out-of-range) are
          // shown INLINE; only non-field (System) errors surface as a toast.
          if (err?.fields && Object.keys(err.fields).length > 0) {
            hydrateServerFieldErrors(err.fields);
            return;
          }
          toast.error(err?.message || t('toasts.draftSaveFailed'));
        },
      },
    );
  };

  // PRD §3.4 — Publish. The re-publish confirm modal gates this when the
  // current saved status is already PUBLISHED; first-time publishes skip it.
  const runPublish = () => {
    warnIfNoLinkedQuestions();
    const pid = item.pid;
    publish.mutate(
      { id: item.id, input: formToSavePayload(form) },
      {
        onSuccess: () => {
          setDirty(false);
          setRepublishOpen(false);
          setFieldErrors({});
          // PRD §3.4 — Saved items always mark the PID dirty + show the PRO
          // Deployment guidance.
          markItemDirty(pid);
          setAfterPublishOpen(true);
          toast.success(t('toasts.saved'));
        },
        onError: (e) => {
          setRepublishOpen(false);
          const err = e as ProItemDesignApiError;
          // §7.1 — field-scoped errors (e.g. duplicate name, out-of-range) are
          // shown INLINE; only non-field (System) errors surface as a toast.
          if (err?.fields && Object.keys(err.fields).length > 0) {
            hydrateServerFieldErrors(err.fields);
            return;
          }
          toast.error(err?.message || t('toasts.saveFailed'));
        },
      },
    );
  };

  const onPublishClick = () => {
    // PRD §8 — client-side pre-validation. Mirrors the BE's PUBLISH checks
    // so users see inline errors before paying the round trip. If a confirm
    // modal would normally appear (re-publish on a PUBLISHED item), it only
    // opens once validation passes.
    const keys = validateItemForPublish(form);
    const translated: Record<string, string> = {};
    for (const [path, k] of Object.entries(keys)) {
      translated[path] = t(`item.validation.${k}`);
    }
    if (Object.keys(translated).length > 0) {
      setFieldErrors(translated);
      toast.error(t('item.validation.summary'));
      return;
    }
    setFieldErrors({});
    if (isDraft) {
      // A draft is never PUBLISHED yet → no re-publish confirm.
      persistDraftThenSave('publish');
      return;
    }
    if (item.status === 'PUBLISHED') {
      setRepublishOpen(true);
      return;
    }
    runPublish();
  };

  const onAfterPublishClose = () => {
    setAfterPublishOpen(false);
    // SHCT-388 — a just-published draft hands selection to its persisted row now
    // that the guidance modal is dismissed.
    if (draftPublishedRealId !== null) {
      const id = draftPublishedRealId;
      setDraftPublishedRealId(null);
      clearDraft();
      select({ type: 'item', id, domainId: domain.id, subAreaId: subArea.id });
    }
  };

  const onAfterDeleteClose = () => {
    setAfterDeleteOpen(false);
    select(null);
  };

  return (
    <>
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="badge badge-primary" style={{ fontSize: 13, fontWeight: 700 }}>
            {inum}
          </span>
          {/* SHCT-388 — the P-#### serial is BE-assigned on Save; hide the badge
              until the draft item is persisted. */}
          {item.pid ? (
            <span
              style={{
                fontSize: 11,
                color: 'var(--muted-foreground)',
                padding: '2px 8px',
                background: 'var(--muted)',
                borderRadius: 4,
              }}
            >
              {item.pid}
            </span>
          ) : null}
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            {t('item.heading')}
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ProStatusBadge status={item.status} />
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            style={{ color: 'var(--destructive)' }}
            onClick={() => setConfirmOpen(true)}
            disabled={!canWrite || deleteItem.isPending}
            {...denyProps}
          >
            <Trash2 size={14} />
            {t('actions.delete')}
          </button>
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {/* Breadcrumb */}
        <div
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Folder size={12} style={{ flexShrink: 0 }} />
          <button
            type="button"
            onClick={onSelectDomainCrumb}
            className="link-text"
            style={crumbButtonStyle}
          >
            {dn}. {domain.name}
          </button>
          <ChevronRight size={12} style={{ flexShrink: 0 }} />
          <button
            type="button"
            onClick={onSelectSubAreaCrumb}
            className="link-text"
            style={crumbButtonStyle}
          >
            {sn}. {subArea.name}
          </button>
          <ChevronRight size={12} style={{ flexShrink: 0 }} />
          <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
            {inum} {item.name}
          </span>
        </div>

        {/* Name */}
        <div className="form-field">
          <label className="form-field__label" htmlFor="item-name">
            {t('item.fields.name.label')}{' '}
            <span className="required" aria-label={t('item.fields.name.required')}>
              *
            </span>
          </label>
          <input
            id="item-name"
            ref={nameRef}
            type="text"
            className={
              nameError
                ? 'form-field__input form-field__input--error'
                : 'form-field__input'
            }
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
            placeholder={t('item.fields.name.placeholder')}
            readOnly={!canWrite}
            {...denyProps}
          />
          {nameError ? <p className="form-field__error">{nameError}</p> : null}
        </div>

        {/* Description */}
        <div className="form-field">
          <label className="form-field__label" htmlFor="item-description">
            {t('item.fields.description.label')}
          </label>
          <textarea
            id="item-description"
            className="form-field__textarea"
            rows={3}
            value={form.description}
            onChange={(e) => updateForm({ description: e.target.value })}
            maxLength={2000}
            readOnly={!canWrite}
            {...denyProps}
          />
        </div>

        {/* Scope */}
        <div className="form-field">
          <label className="form-field__label">
            {t('item.fields.scope.label')}{' '}
            <span className="required" aria-label={t('item.fields.scope.required')}>
              *
            </span>
          </label>
          <div style={{ display: 'flex', gap: 20, marginTop: 4, flexWrap: 'wrap' }}>
            {PRO_ITEM_SCOPES.map((scope) => (
              <label
                key={scope}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  cursor: canWrite ? 'pointer' : 'default',
                }}
              >
                <input
                  type="radio"
                  name="item-scope"
                  value={scope}
                  checked={form.scope === scope}
                  onChange={() =>
                    updateForm({
                      scope,
                      // Clear the alternate field so saved value stays in sync
                      // with the picked scope.
                      diseaseGroupId: scope === 'DISEASE_GROUP' ? form.diseaseGroupId : null,
                      diseaseCodeId: scope === 'DISEASE_SPECIFIC' ? form.diseaseCodeId : null,
                    })
                  }
                  disabled={!canWrite}
                  {...denyProps}
                  style={{ accentColor: 'var(--info)' }}
                />
                {t(`item.fields.scope.options.${scopeI18nKey(scope)}`)}
              </label>
            ))}
          </div>
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: '#f0f7ff',
              borderRadius: 6,
              fontSize: 11,
              color: 'var(--muted-foreground)',
              lineHeight: 1.6,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
            }}
          >
            <Info size={12} color="var(--info)" style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              {t('item.fields.scope.infoBannerPrefix')}
              <Link
                to="/disease-code"
                style={{ color: 'var(--info)', textDecoration: 'underline' }}
              >
                {t('item.fields.scope.infoBannerLink')}
              </Link>
              {t('item.fields.scope.infoBannerSuffix')}
            </span>
          </div>
        </div>

        {/* Conditional disease-group / disease-code pickers */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {form.scope === 'DISEASE_GROUP' ? (
            <div className="form-field">
              <label className="form-field__label" htmlFor="item-disease-group">
                {t('item.fields.diseaseGroup.label')}{' '}
                <span
                  className="required"
                  aria-label={t('item.fields.diseaseGroup.required')}
                >
                  *
                </span>
              </label>
              <select
                id="item-disease-group"
                className="form-field__select"
                value={form.diseaseGroupId ?? ''}
                onChange={(e) =>
                  updateForm({
                    diseaseGroupId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={!canWrite || groups.isLoading}
                {...denyProps}
              >
                <option value="">
                  {groups.isLoading
                    ? t('item.fields.diseaseGroup.loading')
                    : t('item.fields.diseaseGroup.placeholder')}
                </option>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              {groups.isError ? (
                <p className="form-field__error">
                  {t('item.fields.diseaseGroup.loadError')}
                </p>
              ) : fieldErrors.diseaseGroupId ? (
                <p className="form-field__error">{fieldErrors.diseaseGroupId}</p>
              ) : null}
            </div>
          ) : null}

          {form.scope === 'DISEASE_SPECIFIC' ? (
            <div className="form-field">
              <label className="form-field__label" htmlFor="item-disease-code">
                {t('item.fields.diseaseCode.label')}{' '}
                <span
                  className="required"
                  aria-label={t('item.fields.diseaseCode.required')}
                >
                  *
                </span>
              </label>
              <select
                id="item-disease-code"
                className="form-field__select"
                value={form.diseaseCodeId ?? ''}
                onChange={(e) =>
                  updateForm({
                    diseaseCodeId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={!canWrite || codes.isLoading}
                {...denyProps}
              >
                <option value="">
                  {codes.isLoading
                    ? t('item.fields.diseaseCode.loading')
                    : t('item.fields.diseaseCode.placeholder')}
                </option>
                {/* QA33 — only ACTIVE disease codes are selectable. An already-saved
                    code that has since gone INACTIVE stays visible so the current
                    value isn't silently dropped when editing an existing item. */}
                {(codes.data ?? [])
                  .filter(
                    (c) => c.status === 'ACTIVE' || c.id === form.diseaseCodeId,
                  )
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} {c.name}
                    </option>
                  ))}
              </select>
              {codes.isError ? (
                <p className="form-field__error">
                  {t('item.fields.diseaseCode.loadError')}
                </p>
              ) : fieldErrors.diseaseCodeId ? (
                <p className="form-field__error">{fieldErrors.diseaseCodeId}</p>
              ) : null}
              <Link
                to="/disease-code"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 6,
                  fontSize: 11,
                  color: 'var(--info)',
                  textDecoration: 'none',
                }}
              >
                <PlusCircle size={12} />
                {t('item.fields.diseaseCode.registerLink')}
              </Link>
            </div>
          ) : null}
        </div>

        <div className="section-divider" />

        {/* PRD §3.2 — Linked Questionnaires (read-only, auto-synced from
            Questionnaire Design). */}
        <LinkedQuestionsSection linkedQuestions={linkedQuestions} itemPid={item.pid} />

        <div className="section-divider" />

        {/* PRD §3.2 — Measurement Schedule */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Calendar size={14} />
          {t('item.fields.schedule.sectionLabel')}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-field__label" htmlFor="item-cycle">
              {t('item.fields.cycle.label')}{' '}
              <span className="required" aria-label={t('item.fields.cycle.required')}>
                *
              </span>
            </label>
            <select
              id="item-cycle"
              className="form-field__select"
              value={form.cycle}
              onChange={(e) => onCycleChange(e.target.value as ProMeasurementCycle)}
              disabled={!canWrite}
              {...denyProps}
            >
              {PRO_MEASUREMENT_CYCLES.map((c) => (
                <option key={c} value={c}>
                  {t(`item.fields.cycle.options.${c}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-field__label" htmlFor="item-first-execution">
              {t('item.fields.firstExecution.label')}{' '}
              <span
                className="required"
                aria-label={t('item.fields.firstExecution.required')}
              >
                *
              </span>
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {/* SHCT-776 — read-only display, not a picker. The criteria is decided by the
                  measurement cycle above (see onCycleChange), so there is nothing here for an
                  administrator to choose; the requirement document also states it is no longer a
                  validated field. Rendered from the same derivation the handler uses rather than
                  from the stored value, so a legacy row still carrying the abolished
                  `퇴원 후 다음날` displays what its cycle actually means today. */}
              <input
                id="item-first-execution"
                className="form-field__input"
                style={{ flex: 1 }}
                type="text"
                readOnly
                value={t(
                  `item.fields.firstExecution.options.${firstExecutionKindForCycle(form.cycle)}`,
                )}
              />
              {/* QC PID-033 — the N-day input only appears for the
                  "N days after discharge" criterion (hidden otherwise). */}
              {cycleExposesNDays(form.cycle) ? (
                <>
                  {/* SHCT-607 — shared NumberInput so a typed digit does not render
                      with a stray leading zero. */}
                  <NumberInput
                    aria-label={t('item.fields.firstExecution.nDaysTitle')}
                    min={1}
                    max={365}
                    value={form.firstExecutionNDays}
                    onChange={onNDaysChange}
                    disabled={!canWrite}
                    {...denyProps}
                    title={tooltip ?? t('item.fields.firstExecution.nDaysTitle')}
                    style={{
                      width: 60,
                      height: 36,
                      padding: '0 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      fontSize: 13,
                      background: 'var(--input-background)',
                    }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                    {t('item.fields.firstExecution.nDaysSuffix')}
                  </span>
                </>
              ) : null}
            </div>
            {nDaysError ? <p className="form-field__error">{nDaysError}</p> : null}
          </div>
        </div>

        {/* SHCT-609 — 요일 is required by the 측정 주기, not by the 첫 시행 기준
            (PRD 04 v1.22 §3.2 [ADM.055]: the two are separate axes). 주 1회 · 격주 ·
            월 1회 need it; `매일` has all seven implied and `분기별` ignores it
            entirely ([ADM.056] — 첫 실행일 + 90일 × k). Only those two go disabled
            and lose the required marker. */}
        <div className="form-field">
          <label className="form-field__label">
            {t('item.fields.daysOfWeek.label')}
            {daysRequired ? (
              <>
                {' '}
                <span
                  className="required"
                  aria-label={t('item.fields.daysOfWeek.required')}
                >
                  *
                </span>
              </>
            ) : null}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {DAYS_OF_WEEK.map((day) => {
              const isDaily = form.cycle === 'DAILY';
              const checked = isDaily || form.daysOfWeek.includes(day);
              const locked = isDaily || !daysRequired;
              return (
                <label
                  key={day}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 13,
                    cursor: canWrite && !locked ? 'pointer' : 'default',
                    opacity: locked ? 0.7 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleDay(day)}
                    disabled={!canWrite || locked}
                    {...denyProps}
                    style={{ width: 16, height: 16, accentColor: 'var(--info)' }}
                  />
                  {t(`item.fields.daysOfWeek.${day}`)}
                </label>
              );
            })}
          </div>
          {form.cycle === 'QUARTERLY' ? (
            <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '6px 0 0' }}>
              {t('item.fields.daysOfWeek.notAppliedQuarterly')}
            </p>
          ) : null}
          {fieldErrors.daysOfWeek ? (
            <p className="form-field__error">{fieldErrors.daysOfWeek}</p>
          ) : null}
        </div>

        <div className="section-divider" />

        {/* PRD §3.3 — Grade Cutoff (SUM + COMPLEX). Save handlers + cross-
            field validation land in Slice 12 / 13. */}
        <GradeCutoffSection
          gradeSumG1Max={form.gradeSumG1Max}
          gradeSumG2Max={form.gradeSumG2Max}
          // SHCT-394 / SHCT-220 — the item's server id. The guideline link is
          // resolved from the guideline side (`linkedItemId`), so it is immune to
          // unsaved name edits by construction — which is what the old
          // `itemName={item.name}` prop needed a comment to guarantee. A draft
          // item's id is negative and matches no guideline, so it renders the
          // unlinked state, as before.
          itemId={item.id}
          fieldErrors={fieldErrors}
          canWrite={canWrite}
          onG1MaxChange={(v) => updateForm({ gradeSumG1Max: v })}
          onG2MaxChange={(v) => updateForm({ gradeSumG2Max: v })}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            marginTop: 24,
          }}
        >
          {/* QC PID-053/057 — when a required field is missing, Publish is
              disabled and the footer lists the specific blocking reasons live
              (so the user sees *why* without clicking a disabled button). Save
              Draft stays enabled (PRD §3.4 — drafts may be incomplete). */}
          {canWrite && hasPublishErrors ? (
            <span
              role="alert"
              style={{
                fontSize: 11,
                color: 'var(--destructive)',
                marginRight: 'auto',
              }}
            >
              {t('item.validation.blockedPrefix')} {publishBlockers.join(' · ')}
            </span>
          ) : null}
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onSaveDraft}
            disabled={
              !canWrite || createItem.isPending || saveDraft.isPending || publish.isPending
            }
            {...denyProps}
          >
            {createItem.isPending || saveDraft.isPending
              ? t('actions.saving')
              : t('actions.saveDraft')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onPublishClick}
            disabled={
              !canWrite ||
              createItem.isPending ||
              saveDraft.isPending ||
              publish.isPending ||
              hasPublishErrors
            }
            {...denyProps}
          >
            {createItem.isPending || publish.isPending
              ? t('actions.saving')
              : t('actions.publish')}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('item.delete.title')}
        message={t('item.delete.message')}
        confirmLabel={t('item.delete.confirm')}
        cancelLabel={t('actions.cancel')}
        confirmVariant="destructive"
        loading={deleteItem.isPending}
        loadingLabel={t('actions.deleting')}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* PRD §3.4 — re-publish confirm shown only when current saved status is
          already PUBLISHED. First-time publish skips this. */}
      <ConfirmModal
        open={republishOpen}
        title={t('item.republishConfirm.title')}
        message={t('item.republishConfirm.message')}
        confirmLabel={t('actions.continue')}
        cancelLabel={t('actions.cancel')}
        loading={publish.isPending}
        loadingLabel={t('actions.saving')}
        onConfirm={runPublish}
        onCancel={() => setRepublishOpen(false)}
      />

      {/* PRD §3.4 — Post-publish PRO Deployment guidance. */}
      <InfoModal
        open={afterPublishOpen}
        title={t('item.afterPublish.title')}
        body={t('item.afterPublish.message')}
        confirmLabel={t('actions.confirm')}
        onClose={onAfterPublishClose}
      />

      {/* PRD §3.4 — Post-delete PRO Deployment guidance (PUBLISHED items only). */}
      <InfoModal
        open={afterDeleteOpen}
        title={t('item.afterDelete.title')}
        body={t('item.afterDelete.message')}
        confirmLabel={t('actions.confirm')}
        onClose={onAfterDeleteClose}
      />
    </>
  );
}

// Field paths cleared when the user edits one of the corresponding form
// keys. Matches the path strings used by `item-validation.ts` so client and
// server errors stay coherent.
const FIELD_PATHS_FOR_PATCH: Record<keyof ItemFormState, readonly string[]> = {
  name: ['name'],
  description: [],
  scope: ['diseaseGroupId', 'diseaseCodeId'],
  diseaseGroupId: ['diseaseGroupId'],
  diseaseCodeId: ['diseaseCodeId'],
  cycle: ['daysOfWeek'],
  daysOfWeek: ['daysOfWeek'],
  firstExecutionKind: ['schedule.firstExecution.nDays'],
  firstExecutionNDays: ['schedule.firstExecution.nDays'],
  gradeSumG1Max: ['gradeSum.g1Max'],
  gradeSumG2Max: ['gradeSum.g1Max'],
  // Read-only (linked from PRD 07) and not a publish condition (Q75) → no
  // clearable field errors, but the key is kept to satisfy the exhaustive map.
  gradeGuidelines: [],
};

function clearTouchedErrors(
  prev: Record<string, string>,
  patch: Partial<ItemFormState>,
): Record<string, string> {
  const paths = new Set<string>();
  for (const key of Object.keys(patch) as (keyof ItemFormState)[]) {
    for (const p of FIELD_PATHS_FOR_PATCH[key] ?? []) paths.add(p);
  }
  if (paths.size === 0) return prev;
  let next = prev;
  for (const p of paths) {
    if (next[p] !== undefined) {
      if (next === prev) next = { ...prev };
      delete next[p];
    }
  }
  return next;
}

// Maps the wire-format scope enum to the camelCase i18n leaf used in the
// `item.fields.scope.options.*` namespace.
function scopeI18nKey(scope: ProItemScope): 'common' | 'diseaseGroup' | 'diseaseSpecific' {
  switch (scope) {
    case 'COMMON':
      return 'common';
    case 'DISEASE_GROUP':
      return 'diseaseGroup';
    case 'DISEASE_SPECIFIC':
      return 'diseaseSpecific';
  }
}

const crumbButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
  // #2 — long Category / Sub-category names wrap within the crumb instead of
  // overflowing the breadcrumb row.
  textAlign: 'left',
  minWidth: 0,
  wordBreak: 'break-word',
};

// Container wrapping the loading / error / found states so the router only
// needs to pass the item id + tree-derived parents.
interface ItemDetailContainerProps {
  domain: ProDomain;
  domainIndex: number;
  subArea: ProSubArea;
  subAreaIndex: number;
  itemId: number;
  itemIndex: number;
  item: ProItem | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function ItemDetailContainer({
  domain,
  domainIndex,
  subArea,
  subAreaIndex,
  itemIndex,
  item,
  isLoading,
  isError,
}: ItemDetailContainerProps) {
  const { t } = useTranslation('pro-item-design');
  if (isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--muted-foreground)', fontSize: 13 }}>
        {t('item.loading')}
      </div>
    );
  }
  if (isError || !item) {
    return <NotFoundDetail />;
  }
  return (
    <ItemDetail
      domain={domain}
      domainIndex={domainIndex}
      subArea={subArea}
      subAreaIndex={subAreaIndex}
      item={item}
      itemIndex={itemIndex}
    />
  );
}
